import React, {useEffect, useRef, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import {
    Activity,
    AlertCircle,
    ArrowDownRight,
    ArrowLeft,
    ArrowUpRight,
    BarChart3,
    BookOpen,
    Building2,
    CheckCircle,
    Clock,
    Eye,
    FileText,
    GitBranch,
    Info,
    Link as LinkIcon,
    Loader,
    Repeat,
    Shield
} from 'lucide-react';
import {
    useAnalyzeDocumentMutation,
    useGetDocumentContentQuery,
    useGetDocumentQuery,
    useGetDocumentRelatedDocumentsQuery,
    useSendDocumentChatMessageMutation,
    useStartDocumentChatMutation,
    useValidateKeyTermMutation
} from '../store/api/apiSlice';
import {useAuth0} from '@auth0/auth0-react';
import {useSelector} from 'react-redux';
import {LoadingSpinner} from '../components/ui/LoadingSpinner';
import {ExtractedTermsDisplay} from '../components/ui/ExtractedTermsDisplay';
import {ChatPanel} from '../components/ui/ChatPanel';
import {PDFViewer} from '../components/ui/PDFViewer';
import toast from 'react-hot-toast';

interface ProcessingStatus {
    documentId: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    message: string;
    ai_summary?: string;
    ai_analysis?: string;
}

export const DocumentDetailPage: React.FC = () => {
    const {id} = useParams<{ id: string }>();
    const [activeTab, setActiveTab] = useState<'overview' | 'content' | 'analysis' | 'history'>('overview');
    const [processingStatus, setProcessingStatus] = useState<ProcessingStatus | null>(null);
    const wsRef = useRef<WebSocket | null>(null);
    const {getAccessTokenSilently} = useAuth0();

    // Get auth state to determine when API calls should be made
    const auth = useSelector((state: any) => state.auth);
    const isAuthReady = auth.isAuthenticated && !auth.isLoading && auth.token;

    const {
        data: document,
        isLoading: isLoadingDocument,
        error: documentError,
        refetch: refetchDocument
    } = useGetDocumentQuery(id!, {
        skip: !id || !isAuthReady
    });

    const {
        data: documentContent,
        isLoading: isLoadingContent,
        error: contentError,
        refetch: refetchContent
    } = useGetDocumentContentQuery(id!, {
        skip: !id || !isAuthReady
    });

    const {
        data: relatedDocuments,
        isLoading: isLoadingRelated,
        error: relatedError
    } = useGetDocumentRelatedDocumentsQuery(id!, {
        skip: !id || !isAuthReady
    });

    const [analyzeDocument, {isLoading: isAnalyzing}] = useAnalyzeDocumentMutation();
    const [validateKeyTerm] = useValidateKeyTermMutation();
    const [startDocumentChat] = useStartDocumentChatMutation();
    const [sendDocumentChatMessage] = useSendDocumentChatMessageMutation();
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [chatMessage, setChatMessage] = useState<string | undefined>(undefined);

    // WebSocket for real-time updates
    useEffect(() => {
        if (document?.id) {
            const clientId = Math.random().toString(36).substring(7);
            const wsUrl = `ws://localhost:8000/api/v1/documents/ws/${clientId}`;

            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;

            ws.onmessage = (event) => {
                const message = JSON.parse(event.data);
                if (message.type === 'document_update' && message.document_id === document.id) {
                    setProcessingStatus(prev => ({
                        documentId: document.id,
                        status: message.data.status,
                        message: message.data.message,
                        ai_summary: message.data.ai_summary || prev?.ai_summary,
                        ai_analysis: message.data.ai_analysis || prev?.ai_analysis
                    }));

                    if (message.data.status === 'completed') {
                        refetchDocument();
                        refetchContent();
                    }
                }
            };

            return () => {
                ws.close();
            };
        }
    }, [document?.id, refetchDocument, refetchContent]);

    const handleAnalyze = async (analysisType: string) => {
        if (!document?.id) return;

        try {
            await analyzeDocument({id: document.id, analysis_type: analysisType}).unwrap();
            toast.success(`${analysisType.replace('_', ' ')} analysis started`);
        } catch (error: any) {
            toast.error(error?.data?.detail || 'Analysis failed');
        }
    };

    const handleOpenChat = async () => {
        if (!document?.id) return;
        try {
            setIsChatOpen(true);
            setChatMessage('Loading context...');
            const res = await startDocumentChat({documentId: document.id}).unwrap();
            setChatMessage(res.message || 'Chat session is ready.');
        } catch (e) {
            setChatMessage('Failed to start chat session.');
        }
    };

    const handleExtractSLAs = async () => {
        if (!document?.id) return;

        try {
            const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                    scope: "openid profile email"
                }
            });

            const response = await fetch(`/api/v1/documents/${document.id}/extract-slas`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('SLA extraction failed');
            }

            toast.success('SLA extraction started');
        } catch (error: any) {
            toast.error(error?.message || 'SLA extraction failed');
        }
    };

    const handleExtractPaymentDiscounts = async () => {
        if (!document?.id) return;
        try {
            const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                    scope: "openid profile email"
                }
            });

            const response = await fetch(`/api/v1/documents/${document.id}/extract-payment-discounts`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Payment Discount extraction failed');
            }

            toast.success('Payment Discount extraction started');
        } catch (error: any) {
            toast.error(error?.message || 'Payment Discount extraction failed');
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'pending':
                return <Clock className="w-5 h-5 text-yellow-500"/>;
            case 'processing':
                return <Loader className="w-5 h-5 animate-spin text-blue-500"/>;
            case 'completed':
                return <CheckCircle className="w-5 h-5 text-green-500"/>;
            case 'failed':
                return <AlertCircle className="w-5 h-5 text-red-500"/>;
            default:
                return <FileText className="w-5 h-5 text-gray-500"/>;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pending':
                return 'bg-yellow-100 text-yellow-800 border-yellow-300';
            case 'processing':
                return 'bg-blue-100 text-blue-800 border-blue-300';
            case 'completed':
                return 'bg-green-100 text-green-800 border-green-300';
            case 'failed':
                return 'bg-red-100 text-red-800 border-red-300';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-300';
        }
    };

    const formatFileSize = (bytes: number) => {
        if (!bytes) return 'Unknown';
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const parseJsonSafely = (jsonString: string) => {
        try {
            return JSON.parse(jsonString);
        } catch {
            return {raw: jsonString};
        }
    };

    if (isLoadingDocument) {
        return (
            <div className="flex items-center justify-center h-64">
                <LoadingSpinner size="lg"/>
            </div>
        );
    }

    const extractExecutiveSummary = (inputJson?: string) => {
        try {

            if (JSON.parse(inputJson || '{}')?.executiveSummary) {
                return JSON.parse(inputJson || '{}')?.executiveSummary;
            }
            return inputJson;
        } catch (error) {
            return inputJson;
        }
    }

    if (documentError || !document) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4"/>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                        Document not found
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                        The document you're looking for doesn't exist or you don't have access to it.
                    </p>
                    <Link to="/documents" className="btn-primary">
                        Back to Documents
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                    <Link to="/documents" className="btn-secondary">
                        <ArrowLeft className="w-4 h-4 mr-2"/> Back
                    </Link>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {document?.title || document?.original_filename || 'Document'}
                    </h1>
                </div>
                <div className="flex items-center space-x-3">
                    {document && (
                        <span
                            className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
              {document.contract_type || document.document_type || 'Contract'}
            </span>
                    )}
                    {document && (
                        <span
                            className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
              {document.processing_status}
            </span>
                    )}
                    <button onClick={handleOpenChat} className="btn-primary">Chat about this contract</button>
                </div>
            </div>

            {/* Key Dates */}
            {/*{document && (*/}
            {/*  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">*/}
            {/*    {(() => {*/}
            {/*      const exec = document.execution_date ? new Date(document.execution_date) : null;*/}
            {/*      const eff = document.effective_date ? new Date(document.effective_date) : null;*/}
            {/*      const exp = document.expiration_date ? new Date(document.expiration_date) : null;*/}
            {/*      const now = new Date();*/}
            {/*      const expClass = exp*/}
            {/*        ? exp < now*/}
            {/*          ? 'bg-red-50 border-red-200'*/}
            {/*          : (exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24) < 30*/}
            {/*          ? 'bg-yellow-50 border-yellow-200'*/}
            {/*          : 'bg-green-50 border-green-200'*/}
            {/*        : 'bg-gray-50 border-gray-200';*/}
            {/*      return (*/}
            {/*        <>*/}
            {/*          <div className="card p-4">*/}
            {/*            <div className="text-sm text-gray-600 dark:text-gray-400">Execution Date</div>*/}
            {/*            <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">*/}
            {/*              {exec ? exec.toLocaleDateString() : '—'}*/}
            {/*            </div>*/}
            {/*          </div>*/}
            {/*          <div className="card p-4">*/}
            {/*            <div className="text-sm text-gray-600 dark:text-gray-400">Effective Date</div>*/}
            {/*            <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">*/}
            {/*              {eff ? eff.toLocaleDateString() : '—'}*/}
            {/*            </div>*/}
            {/*          </div>*/}
            {/*          <div className={`card p-4 border ${expClass}`}>*/}
            {/*            <div className="text-sm text-gray-600 dark:text-gray-400">Expiration Date</div>*/}
            {/*            <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">*/}
            {/*              {exp ? exp.toLocaleDateString() : '—'}*/}
            {/*            </div>*/}
            {/*          </div>*/}
            {/*        </>*/}
            {/*      );*/}
            {/*    })()}*/}
            {/*  </div>*/}
            {/*)}*/}

            <ChatPanel
                isOpen={isChatOpen}
                title={document ? (document.title || document.original_filename || 'Contract Chat') : 'Contract Chat'}
                initialMessage={chatMessage}
                onClose={() => setIsChatOpen(false)}
                onSend={async (message, history) => {
                    if (!document?.id) return '';
                    const res = await sendDocumentChatMessage({documentId: document.id, message, history}).unwrap();
                    return res.reply;
                }}
            />

            {/* Status Banner */}
            {(processingStatus || document.processing_status !== 'completed') && (
                <div
                    className={`p-4 rounded-lg border ${getStatusColor(processingStatus?.status || document.processing_status)}`}>
                    <div className="flex items-center space-x-3">
                        {getStatusIcon(processingStatus?.status || document.processing_status)}
                        <div className="flex-1">
                            <p className="font-medium">
                                Processing
                                Status: {(processingStatus?.status || document.processing_status).charAt(0).toUpperCase() + (processingStatus?.status || document.processing_status).slice(1)}
                            </p>
                            <p className="text-sm mt-1">
                                {processingStatus?.message || 'Document is being processed with AI analysis'}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex space-x-8">
                    {[
                        {id: 'overview', label: 'Overview', icon: Eye},
                        {id: 'content', label: 'Content', icon: FileText},
                        {id: 'analysis', label: 'AI Analysis', icon: BarChart3},
                        {id: 'history', label: 'History', icon: Clock}
                    ].map((tab) => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id as any)}
                                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                                    activeTab === tab.id
                                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                                }`}
                            >
                                <Icon className="w-4 h-4 mr-2"/>
                                {tab.label}
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* Tab Content */}
            <div className="space-y-6">
                {activeTab === 'overview' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Document Info */}
                        <div className="lg:col-span-2 space-y-6">
                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title">Document Information</h3>
                                </div>
                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Type</p>
                                            <p className="text-sm text-gray-900 dark:text-gray-100">
                                                {document.document_type || 'Not specified'}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Contract
                                                Type</p>
                                            <p className="text-sm text-gray-900 dark:text-gray-100">
                                                {document.contract_type || 'Not specified'}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</p>
                                            <span
                                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(document.processing_status)}`}>
                        {getStatusIcon(document.processing_status)}
                                                <span className="ml-1 capitalize">{document.processing_status}</span>
                      </span>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">File
                                                Size</p>
                                            <p className="text-sm text-gray-900 dark:text-gray-100">
                                                {formatFileSize(document.file_size || 0)}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">MIME
                                                Type</p>
                                            <p className="text-sm text-gray-900 dark:text-gray-100">
                                                {document.mime_type || 'Unknown'}
                                            </p>
                                        </div>
                                    </div>
                                    {document.description && (
                                        <div>
                                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Description</p>
                                            <p className="text-sm text-gray-900 dark:text-gray-100">
                                                {document.description}
                                            </p>
                                        </div>
                                    )}
                                    <div>
                                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Uploaded</p>
                                        <p className="text-sm text-gray-900 dark:text-gray-100">
                                            {formatDate(document.created_at)}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* AI Summary */}
                            {(document.ai_summary || processingStatus?.ai_summary) && (
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">AI Summary</h3>
                                    </div>
                                    <div className="prose dark:prose-invert max-w-none">
                                        <p className="text-gray-700 dark:text-gray-300">
                                            {extractExecutiveSummary(document.ai_summary || processingStatus?.ai_summary)}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Vendor Information */}
                            {document.vendor && (
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">Vendor Information</h3>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="flex items-center space-x-2">
                                            <Building2 className="w-5 h-5 text-gray-400"/>
                                            <div>
                                                <p className="font-medium text-gray-900 dark:text-gray-100">{document.vendor.name}</p>
                                                {document.vendor.vendor_type && (
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">{document.vendor.vendor_type}</p>
                                                )}
                                            </div>
                                        </div>

                                        {document.vendor.contact_name && (
                                            <div className="text-sm">
                                                <p className="font-medium text-gray-700 dark:text-gray-300">Contact: {document.vendor.contact_name}</p>
                                                {document.vendor.contact_email && (
                                                    <p className="text-gray-600 dark:text-gray-400">{document.vendor.contact_email}</p>
                                                )}
                                                {document.vendor.contact_phone && (
                                                    <p className="text-gray-600 dark:text-gray-400">{document.vendor.contact_phone}</p>
                                                )}
                                            </div>
                                        )}

                                        {document.vendor.industry && (
                                            <div className="text-sm">
                                                <span
                                                    className="font-medium text-gray-700 dark:text-gray-300">Industry: </span>
                                                <span
                                                    className="text-gray-600 dark:text-gray-400">{document.vendor.industry}</span>
                                            </div>
                                        )}

                                        {document.vendor.ai_confidence && (
                                            <div className="text-sm">
                                                <span className="font-medium text-gray-700 dark:text-gray-300">AI Confidence: </span>
                                                <span
                                                    className="text-gray-600 dark:text-gray-400">{Math.round(document.vendor.ai_confidence * 100)}%</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Related Documents */}
                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title">Related Documents</h3>
                                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                        <GitBranch className="w-4 h-4 mr-1"/>
                                        Document Relationships
                                    </div>
                                </div>

                                {isLoadingRelated ? (
                                    <div className="flex items-center justify-center py-8">
                                        <LoadingSpinner/>
                                        <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading relationships...</span>
                                    </div>
                                ) : relatedError ? (
                                    <div className="flex items-center justify-center py-8">
                                        <AlertCircle className="w-5 h-5 text-red-500 mr-2"/>
                                        <span className="text-sm text-red-600 dark:text-red-400">Failed to load relationships</span>
                                    </div>
                                ) : relatedDocuments && relatedDocuments.all_relationships && relatedDocuments.all_relationships.length > 0 ? (
                                    <div className="space-y-4">
                                        {/* Governing Documents */}
                                        {relatedDocuments.governed_by && relatedDocuments.governed_by.length > 0 && (
                                            <div>
                                                <h4 className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    <ArrowUpRight className="w-4 h-4 mr-1 text-blue-500"/>
                                                    References ({relatedDocuments.governed_by.length})
                                                </h4>
                                                <div className="space-y-2">
                                                    {relatedDocuments.governed_by.map((relationship) => (
                                                        <div key={relationship.relationship_id}
                                                             className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                                            <div className="flex-1">
                                                                <div className="flex items-center space-x-2">
                                                                    <FileText
                                                                        className="w-4 h-4 text-blue-600 dark:text-blue-400"/>
                                                                    <Link
                                                                        to={`/documents/${relationship.document.id}`}
                                                                        className="font-medium text-blue-700 dark:text-blue-300 hover:underline"
                                                                    >
                                                                        {relationship.document.title || relationship.document.original_filename}
                                                                    </Link>
                                                                </div>
                                                                <div
                                                                    className="mt-1 text-sm text-blue-600 dark:text-blue-400">
                                                                    <span
                                                                        className="font-medium capitalize">{relationship.relationship_type.replace('_', ' ')}</span>
                                                                    {relationship.description && (
                                                                        <span> • {relationship.description}</span>
                                                                    )}
                                                                </div>
                                                                {relationship.confidence_score && (
                                                                    <div
                                                                        className="mt-1 text-xs text-blue-500 dark:text-blue-400">
                                                                        AI
                                                                        Confidence: {Math.round(relationship.confidence_score * 100)}%
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="text-right">
                                                                {relationship.document.contract_type && (
                                                                    <span
                                                                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200 mb-1">
                                    {relationship.document.contract_type}
                                  </span>
                                                                )}
                                                                <p className="text-xs text-blue-500 dark:text-blue-400">
                                                                    {formatDate(relationship.document.created_at)}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Governed Documents */}
                                        {relatedDocuments.governs && relatedDocuments.governs.length > 0 && (
                                            <div>
                                                <h4 className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    <ArrowDownRight className="w-4 h-4 mr-1 text-green-500"/>
                                                    Referenced By ({relatedDocuments.governs.length})
                                                </h4>
                                                <div className="space-y-2">
                                                    {relatedDocuments.governs.map((relationship) => (
                                                        <div key={relationship.relationship_id}
                                                             className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                                            <div className="flex-1">
                                                                <div className="flex items-center space-x-2">
                                                                    <FileText
                                                                        className="w-4 h-4 text-green-600 dark:text-green-400"/>
                                                                    <Link
                                                                        to={`/documents/${relationship.document.id}`}
                                                                        className="font-medium text-green-700 dark:text-green-300 hover:underline"
                                                                    >
                                                                        {relationship.document.title || relationship.document.original_filename}
                                                                    </Link>
                                                                </div>
                                                                <div
                                                                    className="mt-1 text-sm text-green-600 dark:text-green-400">
                                                                    <span
                                                                        className="font-medium capitalize">{relationship.relationship_type.replace('_', ' ')}</span>
                                                                    {relationship.description && (
                                                                        <span> • {relationship.description}</span>
                                                                    )}
                                                                </div>
                                                                {relationship.confidence_score && (
                                                                    <div
                                                                        className="mt-1 text-xs text-green-500 dark:text-green-400">
                                                                        AI
                                                                        Confidence: {Math.round(relationship.confidence_score * 100)}%
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="text-right">
                                                                {relationship.document.contract_type && (
                                                                    <span
                                                                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200 mb-1">
                                    {relationship.document.contract_type}
                                  </span>
                                                                )}
                                                                <p className="text-xs text-green-500 dark:text-green-400">
                                                                    {formatDate(relationship.document.created_at)}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* All Other Relationships */}
                                        {relatedDocuments.all_relationships && relatedDocuments.all_relationships.length > 0 && (
                                            <div>
                                                <h4 className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    <LinkIcon className="w-4 h-4 mr-1 text-purple-500"/>
                                                    All Relationships ({relatedDocuments.all_relationships.length})
                                                </h4>
                                                <div className="space-y-2">
                                                    {relatedDocuments.all_relationships.map((relationship) => {
                                                        const getRelationshipIcon = (type: string) => {
                                                            switch (type.toLowerCase()) {
                                                                case 'amends':
                                                                case 'amendment':
                                                                    return <Repeat
                                                                        className="w-4 h-4 text-orange-500"/>;
                                                                case 'supersedes':
                                                                case 'replaces':
                                                                    return <ArrowUpRight
                                                                        className="w-4 h-4 text-red-500"/>;
                                                                case 'references':
                                                                case 'refers_to':
                                                                    return <LinkIcon
                                                                        className="w-4 h-4 text-blue-500"/>;
                                                                case 'supplements':
                                                                case 'addendum':
                                                                    return <ArrowDownRight
                                                                        className="w-4 h-4 text-green-500"/>;
                                                                default:
                                                                    return <FileText
                                                                        className="w-4 h-4 text-gray-500"/>;
                                                            }
                                                        };

                                                        const getRelationshipColor = (type: string) => {
                                                            switch (type.toLowerCase()) {
                                                                case 'amends':
                                                                case 'amendment':
                                                                    return 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800';
                                                                case 'supersedes':
                                                                case 'replaces':
                                                                    return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
                                                                case 'references':
                                                                case 'refers_to':
                                                                    return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
                                                                case 'supplements':
                                                                case 'addendum':
                                                                    return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
                                                                default:
                                                                    return 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700';
                                                            }
                                                        };

                                                        return (
                                                            <div key={relationship.relationship_id}
                                                                 className={`flex items-center justify-between p-3 rounded-lg border ${getRelationshipColor(relationship.relationship_type)}`}>
                                                                <div className="flex-1">
                                                                    <div className="flex items-center space-x-2">
                                                                        {getRelationshipIcon(relationship.relationship_type)}
                                                                        <Link
                                                                            to={`/documents/${relationship.document.id}`}
                                                                            className="font-medium text-gray-900 dark:text-gray-100 hover:underline"
                                                                        >
                                                                            {relationship.document.title || relationship.document.original_filename}
                                                                        </Link>
                                                                    </div>
                                                                    <div
                                                                        className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                                                                        <span
                                                                            className="font-medium capitalize">{relationship.relationship_type.replace('_', ' ')}</span>
                                                                        {relationship.description && (
                                                                            <span> • {relationship.description}</span>
                                                                        )}
                                                                    </div>
                                                                    {relationship.confidence_score && (
                                                                        <div
                                                                            className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                                                                            AI
                                                                            Confidence: {Math.round(relationship.confidence_score * 100)}%
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="text-right">
                                                                    {relationship.document.contract_type && (
                                                                        <span
                                                                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 mb-1">
                                      {relationship.document.contract_type}
                                    </span>
                                                                    )}
                                                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                                                        {formatDate(relationship.document.created_at)}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center py-8">
                                        <Info className="w-5 h-5 text-gray-400 mr-2"/>
                                        <span className="text-sm text-gray-500 dark:text-gray-400">No related documents found</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="space-y-6">
                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title">AI Analysis</h3>
                                </div>
                                <div className="space-y-3">
                                    <button
                                        onClick={() => handleAnalyze('summary')}
                                        disabled={isAnalyzing}
                                        className="w-full flex items-center justify-center px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                                    >
                                        <BookOpen className="w-4 h-4 mr-2"/>
                                        Generate Summary
                                    </button>
                                    <button
                                        onClick={() => handleAnalyze('risk_analysis')}
                                        disabled={isAnalyzing}
                                        className="w-full flex items-center justify-center px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                                    >
                                        <Shield className="w-4 h-4 mr-2"/>
                                        Risk Analysis
                                    </button>
                                    <button
                                        onClick={() => handleAnalyze('key_terms')}
                                        disabled={isAnalyzing}
                                        className="w-full flex items-center justify-center px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                                    >
                                        <BarChart3 className="w-4 h-4 mr-2"/>
                                        Extract Key Terms
                                    </button>
                                    <button
                                        onClick={() => handleAnalyze('compliance')}
                                        disabled={isAnalyzing}
                                        className="w-full flex items-center justify-center px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                                    >
                                        <CheckCircle className="w-4 h-4 mr-2"/>
                                        Compliance Check
                                    </button>
                                </div>
                            </div>

                            {/* Healthcare Contract Analysis */}
                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title">Healthcare Contract Analysis</h3>
                                </div>
                                <div className="space-y-3">
                                    <button
                                        onClick={() => handleExtractSLAs()}
                                        disabled={isAnalyzing}
                                        className="w-full flex items-center justify-center px-4 py-2 text-sm border border-blue-300 dark:border-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-800 transition-colors disabled:opacity-50 text-blue-700 dark:text-blue-300"
                                    >
                                        <Activity className="w-4 h-4 mr-2"/>
                                        Extract SLAs
                                    </button>
                                    <button
                                        onClick={() => handleExtractPaymentDiscounts()}
                                        disabled={isAnalyzing}
                                        className="w-full flex items-center justify-center px-4 py-2 text-sm border border-amber-300 dark:border-amber-600 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-800 transition-colors disabled:opacity-50 text-amber-700 dark:text-amber-300"
                                    >
                                        <BarChart3 className="w-4 h-4 mr-2"/>
                                        Extract Payment Discounts
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'content' && (
                    <div className="space-y-6">
                        {/* PDF Viewer - Show for PDF documents */}
                        {document?.mime_type === 'application/pdf' && (
                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title">PDF Document</h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        Interactive PDF viewer with navigation controls
                                    </p>
                                </div>
                                <PDFViewer
                                    url={`/api/v1/documents/${document.id}/download`}
                                    filename={document.original_filename}
                                    className="border-0"
                                />
                            </div>
                        )}

                        {/* Extracted Text Content */}
                              <div className="card">
                                  <div className="card-header">
                                      <h3 className="card-title">Extracted Text Content</h3>
                                      <p className="text-sm text-gray-500 dark:text-gray-400">
                                          AI-extracted text content from the document
                                      </p>
                                  </div>
                                  {isLoadingContent ? (
                                      <div className="flex items-center justify-center py-8">
                                          <LoadingSpinner/>
                                      </div>
                                  ) : documentContent?.extracted_text ? (
                                      <div className="prose dark:prose-invert max-w-none">
                        <pre
                            className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300 font-mono bg-gray-50 dark:bg-gray-800 p-4 rounded-lg overflow-x-auto">
                          {documentContent.extracted_text}
                        </pre>
                                      </div>
                                  ) : (
                                      <div className="text-center py-8">
                                          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4"/>
                                          <p className="text-gray-500 dark:text-gray-400">
                                              Content extraction is in progress or failed. Please wait for processing to
                                              complete.
                                          </p>
                                      </div>
                                  )}
                              </div>
                    </div>
                )}

                {activeTab === 'analysis' && (
                    <ExtractedTermsDisplay
                        documentId={document.id}
                        documentText={documentContent?.extracted_text || ''}
                        documentUrl={document?.mime_type === 'application/pdf' ? `/api/v1/documents/${document.id}/download` : undefined}
                        documentFilename={document.original_filename}
                        onTermUpdate={(termId, newValue, status) => {
                            validateKeyTerm({
                                documentId: document.id,
                                termId,
                                validation_status: status,
                                updated_value: newValue,
                            })
                                .unwrap()
                                .then(() => {
                                    toast.success('Term validation saved');
                                })
                                .catch((e: any) => {
                                    toast.error(e?.data?.detail || 'Failed to save validation');
                                });
                        }}
                    />
                )}

                {activeTab === 'history' && (
                    <div className="card">
                        <div className="card-header">
                            <h3 className="card-title">Processing History</h3>
                        </div>
                        <div className="space-y-4">
                            <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <CheckCircle className="w-5 h-5 text-green-500"/>
                                <div>
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        Document Uploaded
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        {formatDate(document.created_at)}
                                    </p>
                                </div>
                            </div>
                            {document.updated_at && document.updated_at !== document.created_at && (
                                <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                    <Clock className="w-5 h-5 text-blue-500"/>
                                    <div>
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                            Document Updated
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {formatDate(document.updated_at)}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}; 